import React, { useEffect } from "react";
import { useTranslation } from "../i18n";
import "./MessageModal.css";

export type MessageType = "success" | "error" | "info" | "warning";

interface MessageModalProps {
  isOpen: boolean;
  type: MessageType;
  title: string;
  message: string;
  onClose: () => void;
  autoCloseDelay?: number; // 自动关闭延迟时间（毫秒），0表示不自动关闭
}

const MessageModal: React.FC<MessageModalProps> = ({
  isOpen,
  type,
  title,
  message,
  onClose,
  autoCloseDelay = 3000, // 默认3秒后自动关闭
}) => {
  const { t } = useTranslation();

  useEffect(() => {
    if (isOpen && autoCloseDelay > 0) {
      const timer = setTimeout(() => {
        onClose();
      }, autoCloseDelay);

      return () => clearTimeout(timer);
    }
  }, [isOpen, autoCloseDelay, onClose]);

  if (!isOpen) return null;

  const getIcon = () => {
    switch (type) {
      case "success":
        return (
          <svg
            width="48"
            height="48"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle cx="12" cy="12" r="10" fill="#4CAF50" />
            <path
              d="M9 12l2 2 4-4"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        );
      case "error":
        return (
          <svg
            width="48"
            height="48"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle cx="12" cy="12" r="10" fill="#F44336" />
            <path
              d="M15 9l-6 6M9 9l6 6"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        );
      case "warning":
        return (
          <svg
            width="48"
            height="48"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M12 2L2 19h20L12 2z" fill="#FF9800" />
            <path
              d="M12 8v6M12 16h.01"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        );
      case "info":
        return (
          <svg
            width="48"
            height="48"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle cx="12" cy="12" r="10" fill="#2196F3" />
            <path
              d="M12 8v6M12 16h.01"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        );
      default:
        return null;
    }
  };

  const getButtonColor = () => {
    switch (type) {
      case "success":
        return "#4CAF50";
      case "error":
        return "#F44336";
      case "warning":
        return "#FF9800";
      case "info":
        return "#2196F3";
      default:
        return "#4CAF50";
    }
  };

  return (
    <div className="message-modal-overlay" onClick={onClose}>
      <div
        className={`message-modal message-modal-${type}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="message-modal-content">
          <div className="message-icon">{getIcon()}</div>
          <h3 className="message-title">{title}</h3>
          <p className="message-text">{message}</p>
          <button
            className="message-close-btn"
            onClick={onClose}
            style={{ backgroundColor: getButtonColor() }}
          >
            {t.videoCall.ok}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MessageModal;
